/**
 * Months, for filtering dead bird reports (ROADMAP B12).
 *
 * The export covers the whole year and the work is done a month at a time, so
 * the page needs the months a file actually contains. Dates are read as text
 * and never through `new Date(...)`: "2026-10-01" parsed that way is midnight
 * UTC, which is still September on the Pacific coast, and a report would be
 * filed under the wrong month for exactly the people reading it.
 */

export const UNDATED = "undated";

const NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

/** "2026-01" for a report date, or "" when it cannot be read.
 *  Accepts 2026-01-07, 2026-01-07 13:17:00, 1/7/2026 and 01/07/2026. */
export function monthKey(date: string): string {
    const text = String(date ?? "").trim();
    let year = "";
    let month = 0;

    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
    if (m) {
        year = m[1];
        month = parseInt(m[2], 10);
    } else {
        m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
        if (m) {
            year = m[3];
            month = parseInt(m[1], 10);
        }
    }
    if (!year || month < 1 || month > 12) return "";
    return `${year}-${String(month).padStart(2, "0")}`;
}

/** The key a row is filed under: its month, or UNDATED. */
export function monthOf(date: string): string {
    return monthKey(date) || UNDATED;
}

/** "January 2026", or "No date". */
export function monthLabel(key: string): string {
    if (key === UNDATED) return "No date";
    const m = /^(\d{4})-(\d{2})$/.exec(key);
    return m ? `${NAMES[parseInt(m[2], 10) - 1]} ${m[1]}` : key;
}

export interface MonthOption {
    key: string;
    label: string;
    count: number;
}

/** The months present, newest first, with how many reports each holds. Reports
 *  with no readable date are listed last rather than dropped. */
export function monthsOf(rows: { date: string }[]): MonthOption[] {
    const counts = new Map<string, number>();
    for (const r of rows) {
        const k = monthOf(r.date);
        counts.set(k, (counts.get(k) || 0) + 1);
    }
    const dated = Array.from(counts.keys()).filter((k) => k !== UNDATED).sort().reverse();
    const keys = counts.has(UNDATED) ? [...dated, UNDATED] : dated;
    return keys.map((key) => ({ key, label: monthLabel(key), count: counts.get(key) || 0 }));
}

/** The month to show first: this month if the reports include one, otherwise
 *  "" (every month). Never a month the list does not offer, which would leave
 *  the selector showing nothing while the tables hold something else. Takes the
 *  clock as an argument and reads its local fields, so "this month" is the
 *  month on the person's calendar rather than in UTC. */
export function defaultMonth(options: { key: string }[], now: Date = new Date()): string {
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return options.some((o) => o.key === key) ? key : "";
}

/** The rows in one month, or all of them when `key` is empty. */
export function inMonth<T extends { date: string }>(rows: T[], key: string): T[] {
    return key ? rows.filter((r) => monthOf(r.date) === key) : rows;
}
